import { useState } from 'react';
import { Plus, Pencil, Check, X } from 'lucide-react';
import type { CarBrand, CarFuelType, CarLeasingCompany, CarInsuranceCompany } from '../../types/companyCars';

interface ReferenceDataTabProps {
    brands: CarBrand[];
    fuelTypes: CarFuelType[];
    leasingCompanies: CarLeasingCompany[];
    insuranceCompanies: CarInsuranceCompany[];
    canEdit: boolean;
    onCreateBrand: (name: string) => Promise<number>;
    onUpdateBrand: (id: number, data: { brandName?: string; isActive?: boolean }) => Promise<void>;
    onCreateFuelType: (data: { fuelCode: string; fuelLabel: string; isElectric?: boolean }) => Promise<void>;
    onUpdateFuelType: (id: number, data: { fuelLabel?: string; isElectric?: boolean; isActive?: boolean }) => Promise<void>;
    onCreateLeasingCompany: (name: string) => Promise<void | number>;
    onUpdateLeasingCompany: (id: number, data: { companyName?: string; isActive?: boolean }) => Promise<void>;
    onCreateInsuranceCompany: (name: string) => Promise<number>;
    onUpdateInsuranceCompany: (id: number, data: { companyName?: string; isActive?: boolean }) => Promise<void>;
}

function LookupSection<T extends { Id: number; IsActive: boolean; CreatedBy: string; CreatedAt: string; UpdatedBy: string | null }>({
    title,
    items,
    canEdit,
    getLabel: getLabel,
    onAdd,
    onUpdate,
    renderEditForm,
    renderAddForm,
}: {
    title: string;
    items: T[];
    canEdit: boolean;
    getLabel: (item: T) => string;
    onAdd: (value: string) => Promise<void | number>;
    onUpdate: (id: number, data: Record<string, unknown>) => Promise<void>;
    renderEditForm?: (item: T, onSave: (data: Record<string, unknown>) => void, onCancel: () => void) => React.ReactNode;
    renderAddForm?: (onSave: (value: string) => void, onCancel: () => void) => React.ReactNode;
}) {
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [newValue, setNewValue] = useState('');

    const handleStartEdit = (item: T) => {
        setEditingId(item.Id);
        setEditValue(getLabel(item));
    };

    const handleSave = async () => {
        if (!editValue.trim()) return;
        await onUpdate(editingId!, { [title === 'Car Brands' ? 'brandName' : title === 'Fuel Types' ? 'fuelLabel' : 'companyName']: editValue.trim() });
        setEditingId(null);
    };

    const handleAdd = async () => {
        if (!newValue.trim()) return;
        await onAdd(newValue.trim());
        setNewValue('');
        setIsAdding(false);
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800">{title}</h3>
                {canEdit && (
                    <button
                        onClick={() => { setIsAdding(true); setNewValue(''); }}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                        <Plus className="w-4 h-4" /> Add
                    </button>
                )}
            </div>

            {isAdding && (
                <div className="flex items-center gap-2 mb-2 p-2 bg-blue-50 rounded">
                    {renderAddForm ? renderAddForm(handleAdd as any, () => setIsAdding(false)) : (
                        <>
                            <input
                                type="text"
                                value={newValue}
                                onChange={e => setNewValue(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setIsAdding(false); }}
                                className="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="Enter name..."
                                autoFocus
                            />
                            <button onClick={handleAdd} className="p-1 text-green-600 hover:text-green-800"><Check className="w-4 h-4" /></button>
                            <button onClick={() => setIsAdding(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                        </>
                    )}
                </div>
            )}

            <div className="divide-y divide-gray-100">
                {items.map(item => {
                    const isEditing = editingId === item.Id;
                    return (
                        <div key={item.Id} className={`flex items-center justify-between py-1.5 px-1 text-sm ${!item.IsActive ? 'opacity-50' : ''}`}>
                            {isEditing && !renderEditForm ? (
                                <div className="flex items-center gap-2 flex-1">
                                    <input
                                        type="text"
                                        value={editValue}
                                        onChange={e => setEditValue(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditingId(null); }}
                                        className="flex-1 px-2 py-0.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        autoFocus
                                    />
                                    <button onClick={handleSave} className="p-1 text-green-600 hover:text-green-800"><Check className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                                </div>
                            ) : isEditing && renderEditForm ? (
                                renderEditForm(item, (data) => { onUpdate(item.Id, data); setEditingId(null); }, () => setEditingId(null))
                            ) : (
                                <>
                                    <span className="text-gray-700">{getLabel(item)}</span>
                                    <div className="flex items-center gap-1">
                                        {!item.IsActive && <span className="text-xs text-gray-400">inactive</span>}
                                        {canEdit && (
                                            <>
                                                <button onClick={() => handleStartEdit(item)} className="p-1 text-gray-400 hover:text-blue-600">
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => onUpdate(item.Id, { isActive: !item.IsActive })}
                                                    className={`text-xs px-1.5 py-0.5 rounded ${item.IsActive ? 'text-red-500 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                                                >
                                                    {item.IsActive ? 'Deactivate' : 'Activate'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
                {items.length === 0 && <p className="text-sm text-gray-400 py-2">No entries yet</p>}
            </div>
        </div>
    );
}

export default function ReferenceDataTab({
    brands, fuelTypes, leasingCompanies, insuranceCompanies, canEdit,
    onCreateBrand, onUpdateBrand,
    onCreateFuelType, onUpdateFuelType,
    onCreateLeasingCompany, onUpdateLeasingCompany,
    onCreateInsuranceCompany, onUpdateInsuranceCompany,
}: ReferenceDataTabProps) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <LookupSection
                title="Car Brands"
                items={brands}
                canEdit={canEdit}
                getLabel={b => b.BrandName}
                onAdd={onCreateBrand}
                onUpdate={(id, data) => onUpdateBrand(id, data as any)}
            />

            <LookupSection
                title="Fuel Types"
                items={fuelTypes}
                canEdit={canEdit}
                getLabel={f => `${f.FuelLabel} (${f.FuelCode})${f.IsElectric ? ' ⚡' : ''}`}
                onAdd={async (label) => {
                    const code = label.toUpperCase().replace(/[^A-Z_]/g, '_').slice(0, 20);
                    await onCreateFuelType({ fuelCode: code, fuelLabel: label });
                }}
                onUpdate={(id, data) => onUpdateFuelType(id, data as any)}
            />

            <LookupSection
                title="Leasing Companies"
                items={leasingCompanies}
                canEdit={canEdit}
                getLabel={l => l.CompanyName}
                onAdd={onCreateLeasingCompany}
                onUpdate={(id, data) => onUpdateLeasingCompany(id, data as any)}
            />

            <LookupSection
                title="Insurance Companies"
                items={insuranceCompanies}
                canEdit={canEdit}
                getLabel={i => i.CompanyName}
                onAdd={onCreateInsuranceCompany}
                onUpdate={(id, data) => onUpdateInsuranceCompany(id, data as any)}
            />
        </div>
    );
}
